type AppNameProps = {
  name?: unknown
  settings?: {
    app_name?: unknown
  }
}

const isResolvedAppName = (value: unknown): value is string => {
  return typeof value === 'string' && value.trim().length > 0
}

/**
 * Resolve the application name from the server-provided Inertia shared prop.
 *
 * Sourced from config('app.name') / AppSetting via HandleInertiaRequests,
 * checking `name` and falling back to `settings.app_name`.
 * When the prop is missing or invalid, an empty string is returned so callers
 * never render a brand literal that bypasses the dynamic source of truth.
 */
export const resolveAppName = (props: AppNameProps): string => {
  if (isResolvedAppName(props.name)) {
    return props.name.trim()
  }

  if (props.settings && isResolvedAppName(props.settings.app_name)) {
    return props.settings.app_name.trim()
  }

  return ''
}

/**
 * Format a page title with the dynamic application name.
 *
 * Ensures that if a title already contains the application name as a suffix
 * or is identical to it, the application name is not appended again.
 */
export const formatPageTitle = (
  title: string | undefined | null,
  resolvedAppName: string,
): string => {
  const cleanTitle = typeof title === 'string' ? title.trim() : ''
  const cleanAppName =
    typeof resolvedAppName === 'string' ? resolvedAppName.trim() : ''

  if (!cleanTitle) {
    return cleanAppName
  }

  if (!cleanAppName) {
    return cleanTitle
  }

  if (
    cleanTitle === cleanAppName ||
    cleanTitle.endsWith(` - ${cleanAppName}`) ||
    cleanTitle.endsWith(` — ${cleanAppName}`) ||
    cleanTitle.includes(cleanAppName)
  ) {
    return cleanTitle
  }

  return `${cleanTitle} - ${cleanAppName}`
}
